export default {
  name: 'tabs',
  component(settings) {
    return {
      // Initialize component
      activeTab: null,
      tabTitles: [],

      get tabItems() {
        return this.$el.querySelectorAll('.tab-item');
      },

      get tabButtons() {
        return this.$el.querySelectorAll('.tab-button');
      },

      init() {
        // Collect tab titles from DOM
        this.collectTabTitles();
        
        // Initialize the active tab
        this.initializeActiveTab();
        
        // Set initial container height
        this.$nextTick(() => {
          if (this.activeTab) {
            this.updateContainerHeight(this.activeTab);
          }
        });
      },

      // Collect tab titles from the DOM elements
      collectTabTitles() {
        this.tabTitles = [];
        this.tabItems.forEach((item, index) => {
          const blockId = item.dataset.blockId;
          
          // Try to get title from data attribute first
          let title = item.dataset.title;
          
          // If no data-title, look for title in any form elements or text content
          if (!title) {
            // Look for any input with name containing 'title'
            const titleInput = item.querySelector('input[name*="title"], input[id*="title"]');
            if (titleInput) {
              title = titleInput.value;
            }
          }
          
          // Fallback to default title with index
          if (!title || title.trim() === '') {
            title = `Tab ${index + 1}`;
          }
          
          this.tabTitles.push({
            id: blockId,
            title: title
          });
        });
      },

      // Initialize the active tab
      initializeActiveTab() {
        let firstVisibleTabFound = false;
        
        this.tabItems.forEach((item) => {
          const blockId = item.dataset.blockId;
          const style = window.getComputedStyle(item);
          const isVisible = style.getPropertyValue('display') !== 'none';
          
          // Find corresponding tab block settings to check for active_by_default
          const hasActiveByDefault = this.hasActiveByDefault(blockId);
          
          // Determine if this tab should be active
          if (hasActiveByDefault && isVisible) {
            // If this tab is marked as active by default and is visible, activate it
            this.activeTab = blockId;
          } else if (!firstVisibleTabFound && isVisible && !this.hasAnyActiveByDefault()) {
            // If no tabs are marked as active by default, activate the first visible tab
            this.activeTab = blockId;
            firstVisibleTabFound = true;
          }
        });

        // If no active tab was set, activate the first tab
        if (!this.activeTab && this.tabItems.length > 0) {
          this.activeTab = this.tabItems[0].dataset.blockId;
        }
        
        // Ensure the active tab is initially visible
        if (this.activeTab) {
          const activeTabElement = this.$el.querySelector(`#tab-content-${this.activeTab}`);
          if (activeTabElement) {
            activeTabElement.style.display = 'block';
          }
        }
      },

      // Helper method to check if a specific tab has active_by_default set
      hasActiveByDefault(blockId) {
        const item = Array.from(this.tabItems).find(item => item.dataset.blockId === blockId);
        return item ? item.dataset.activeByDefault === 'true' : false;
      },
      
      // Helper method to check if any tabs have active_by_default set
      hasAnyActiveByDefault() {
        return Array.from(this.tabItems).some(item => item.dataset.activeByDefault === 'true');
      },

      // Set active tab
      setActiveTab(blockId) {
        // Update container height before switching
        this.updateContainerHeight(blockId);
        
        this.activeTab = blockId;
        
        // Update aria-selected attributes on tab buttons
        this.tabButtons.forEach((button, index) => {
          const isActive = button.getAttribute('aria-controls') === `tab-content-${blockId}`;
          button.setAttribute('aria-selected', isActive.toString());
        });
      },

      // Update container height to match the active tab content
      updateContainerHeight(newActiveTabId) {
        const container = this.$refs.tabContainer || this.$el.querySelector('.tab-content');
        const newActiveTab = this.$el.querySelector(`#tab-content-${newActiveTabId}`);
        
        if (container && newActiveTab) {
          // Temporarily show the new tab to measure its height
          const originalDisplay = newActiveTab.style.display;
          const originalVisibility = newActiveTab.style.visibility;
          const originalPosition = newActiveTab.style.position;
          
          // Make it visible but hidden for measurement
          newActiveTab.style.visibility = 'hidden';
          newActiveTab.style.display = 'block';
          newActiveTab.style.position = 'static';
          
          // Measure the height
          const newHeight = newActiveTab.scrollHeight;
          
          // Reset the tab styles
          newActiveTab.style.visibility = originalVisibility;
          newActiveTab.style.display = originalDisplay;
          newActiveTab.style.position = originalPosition;
          
          // Set the container height with transition - use auto height for better content fit
          container.style.transition = 'min-height 300ms ease-in-out';
          // container.style.minHeight = `${Math.max(newHeight + 20, 200)}px`; // Add 20px padding
          container.style.height = 'auto';
        }
      },

      // Check if specific tab is active
      isActive(blockId) {
        return this.activeTab === blockId;
      },

      // Method to go to next tab
      nextTab() {
        const currentIndex = Array.from(this.tabItems).findIndex(
          item => item.dataset.blockId === this.activeTab
        );
        const nextIndex = (currentIndex + 1) % this.tabItems.length;
        const nextBlockId = this.tabItems[nextIndex].dataset.blockId;
        this.setActiveTab(nextBlockId);
      },

      // Method to go to previous tab
      prevTab() {
        const currentIndex = Array.from(this.tabItems).findIndex(
          item => item.dataset.blockId === this.activeTab
        );
        const prevIndex = currentIndex === 0 ? this.tabItems.length - 1 : currentIndex - 1;
        const prevBlockId = this.tabItems[prevIndex].dataset.blockId;
        this.setActiveTab(prevBlockId);
      },

      // Method to go to specific tab by index
      goToTab(index) {
        if (this.tabItems[index]) {
          const blockId = this.tabItems[index].dataset.blockId;
          this.setActiveTab(blockId);
        }
      }
    };
  }
};